import { useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { getDocumentName } from '@e-socna/data-layer';
import { EMPTY_ID } from '@e-socna/empty-client';
import { buildPath } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import {
  PATH_RESOLVABLE_MISSION_TYPE_GENERAL,
  PATH_RESOLVABLE_MISSION_TYPES,
} from '../../../app/constants/paths.constant';
import { ResolvableMissionTypePage_OneResolvableMissionType_QueryDocument } from '../../../pages/ResolvableMissionType/hooks/useFindOneResolvableMissionTypeQuery.hook';
import { ResolvableMissionTypeListCmp_AllResolvableMissionTypes_QueryDocument } from '../../../components/ResolvableMissionTypeList/hooks/useFindAllResolvableMissionTypesQuery.hook';
import { userFriendlyErrors } from '../../../utils/back-errors.util';
import { useViewSnackbarAction } from '../../../hooks/useViewSnackbarAction.hook';
import type { BackError } from '../../../utils/back-errors.util';
import type { WizardSubmitHandlerParams } from '../../../components/Wizard/types/submit-handler.type';

import { FormVariant } from '../../enums/variant.enum';
import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';

import { resolvableMissionTypeForm2ApiMapper } from '../mappers/resolvable-mission-type.form2api.mapper';
import type { ResolvableMissionTypeForm_ResolvableMissionType } from '../fragments/resolvable-mission-type.fragment';
import type { ResolvableMissionTypeFormTab } from '../enums/tab.enum';

import { useCreateResolvableMissionTypeMutation } from './useCreateResolvableMissionTypeMutation.hook';
import { useUpdateResolvableMissionTypeMutation } from './useUpdateResolvableMissionTypeMutation.hook';
import type { ResolvableMissionType_Create_Result } from './useCreateResolvableMissionTypeMutation.hook';
import type { ResolvableMissionType_Foau_Result } from './useUpdateResolvableMissionTypeMutation.hook';

export type ResolvableMissionTypeMutationResult =
  ResolvableMissionType_Create_Result | ResolvableMissionType_Foau_Result;

interface Params {
  readonly formVariant?: FormVariant;
  readonly handleSuccess?: (
    result: ResolvableMissionTypeMutationResult,
  ) => void;
  readonly resolvableMissionTypeId?: Id;
}

export const useResolvableMissionTypeSubmit = ({
  formVariant,
  handleSuccess,
  resolvableMissionTypeId,
}: Readonly<Params>) => {
  const { enqueueSnackbar } = useSnackbar();
  const { buildViewSnackbarAction } = useViewSnackbarAction();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate: createMutation } = useCreateResolvableMissionTypeMutation();
  const { mutate: updateMutation } = useUpdateResolvableMissionTypeMutation();

  const handleSubmit = useCallback(
    ({
      values,
      memos,
      formikHelpers: { setSubmitting },
      setWizardError,
    }: Readonly<
      WizardSubmitHandlerParams<
        ResolvableMissionTypeForm_ResolvableMissionType,
        ResolvableMissionTypeFormTab
      >
    >) => {
      setWizardError(undefined);

      const onSuccess = (result: ResolvableMissionTypeMutationResult) => {
        if (formVariant === FormVariant.Creation) {
          const { _id } = (result as ResolvableMissionType_Create_Result)
            .createResolvableMissionType;

          enqueueSnackbar('Votre nouveau type de mission a bien été créé.', {
            action: buildViewSnackbarAction(
              buildPath(PATH_RESOLVABLE_MISSION_TYPE_GENERAL, { id: _id }),
            ),
          });
          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(
                ResolvableMissionTypeListCmp_AllResolvableMissionTypes_QueryDocument,
              ),
            ],
          });
          if (handleSuccess) {
            handleSuccess(result);
          } else {
            void navigate(PATH_RESOLVABLE_MISSION_TYPES);
          }
        } else {
          enqueueSnackbar('Votre type de mission a bien été mis à jour.');
          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(
                ResolvableMissionTypePage_OneResolvableMissionType_QueryDocument,
              ),
              { id: resolvableMissionTypeId },
            ],
          });
          if (handleSuccess) {
            handleSuccess(result);
          }
        }
        setSubmitting(false);
      };

      const onError = (error: unknown) => {
        setWizardError({
          messages: userFriendlyErrors(error as BackError),
        });

        setSubmitting(false);
      };

      if (formVariant === FormVariant.Creation) {
        createMutation(
          {
            resolvableMissionType: resolvableMissionTypeForm2ApiMapper(values),
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onError,
            onSuccess,
          },
        );
      } else {
        updateMutation(
          {
            filter: { _id: resolvableMissionTypeId ?? EMPTY_ID },
            update: resolvableMissionTypeForm2ApiMapper(values),
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onError,
            onSuccess,
          },
        );
      }

      return undefined;
    },
    [
      buildViewSnackbarAction,
      createMutation,
      enqueueSnackbar,
      formVariant,
      handleSuccess,
      resolvableMissionTypeId,
      navigate,
      queryClient,
      updateMutation,
    ],
  );

  return { handleSubmit };
};
